import type {
    ApplyBidResultModel,
    BidInfoToGenerateTitleEditModel,
    BidInfoToGenerateTitleModel,
    DistributionBidEditModel,
    DistributionBidListModel,
    OrganizationShippingOrderProcessingSettingEditModel,
    ShippingOrderBindModel,
    ShippingOrderDistributionBidEditModel,
    ShippingOrderGenerateDeclineResponseTitleModel,
    ShippingOrderGenerateResponseTitleByBidModel,
    ShippingOrderListModel,
    ShippingOrderModel,
    ShippingOrderTitleModel,
} from "api";
import { OdataFilters, queryCollection, queryParams } from "helpers/reqparams";
import { alert } from "services/alert";
import { alertRequest, body, request } from "services/request";

interface ApplyDistributionBidCommandResult {
    isOk: boolean;
    message?: string | null;
    addition?: ApplyBidResultModel | null;
}

interface ShippingOrderSelectParams {
    start?: number;
    length?: number;
    filters?: Array<{ dataIndex: string; value: string | number | Array<string | number> }>;
}

const resource = "api/shippingOrders";
const shippingOrdersQuery = {
    orderBy: ["id desc"],
};

export const index = ({ preparedFilter, queryFilter, ...params }: OdataFilters) => {
    const qp = new URLSearchParams(queryFilter as URLSearchParams);
    const query = queryCollection(params, { filter: preparedFilter, ...shippingOrdersQuery }).substr(1);
    const queryString = [qp.toString(), query].filter((item) => item.length).join("&");
    return request(`${resource}/getList?${queryString}`, { method: "GET" }, { countInHeaders: true });
};

export const getItem = (id: number): Promise<ShippingOrderModel | false> =>
    request(`${resource}/Get/${id}`, { method: "GET" });

export const getByDistributionBidId = (distributionBidId: number): Promise<ShippingOrderModel | null> => {
    const query = queryCollection({ start: 0, length: 1 }, { filter: { distributionBidId }, ...shippingOrdersQuery });

    return request(`${resource}/getList${query}`, { method: "GET" }).then((items: ShippingOrderListModel[] = []) => {
        const [shippingOrder] = items;

        return shippingOrder ? getItem(shippingOrder.id).then((item) => item || null) : null;
    });
};

export const getForDistributionBid = (shippingOrderId: number): Promise<DistributionBidEditModel | false> =>
    request(`${resource}/getForDistributionBid/${shippingOrderId}`, { method: "GET" });

export const getAvailableDistributionBids = (
    params: ShippingOrderSelectParams,
): Promise<DistributionBidListModel[]> => {
    const idFilter = params.filters?.find(({ dataIndex }) => dataIndex === "id")?.value;
    const requestedIds = (Array.isArray(idFilter) ? idFilter : [idFilter]).reduce<number[]>((result, value) => {
        const id = Number(value);
        if (Number.isInteger(id) && id > 0) {
            result.push(id);
        }
        return result;
    }, []);
    const requestedCurrentDistributionBid = Array.isArray(idFilter) && requestedIds.length > 0;
    const filter = requestedCurrentDistributionBid
        ? { id: { in: requestedIds } }
        : {
              isDeleted: false,
              ...(requestedIds.length > 0 ? { id: requestedIds[0] } : {}),
          };
    const query = queryCollection(
        { start: params.start, length: params.length, filters: [] },
        { filter, orderBy: "id desc" },
    );

    return request(`api/distributionBids/getList${query}`, { method: "GET" })
        .then((response) => (Array.isArray(response) ? response : []))
        .catch(() => []);
};

export const applyDistributionBid = (
    params: ShippingOrderDistributionBidEditModel,
): Promise<ApplyBidResultModel | false> =>
    alertRequest(`${resource}/applyDistributionBid`, body(params), {
        successAlerts: false,
        noContentAlerts: true,
    }).then((response: ApplyDistributionBidCommandResult | ApplyBidResultModel | false) => {
        if (!response) {
            return false;
        }

        let result: ApplyBidResultModel | null = null;
        let errorMessage: string | null = null;

        if ("isOk" in response) {
            result = response.addition || null;
            errorMessage = response.isOk ? null : response.message || "Не удалось создать заказ по ЭЗЗ.";
        } else {
            result = response;
        }

        if (errorMessage || !result || typeof result.id !== "number") {
            alert({
                type: "error",
                title: "Ошибка.",
                message: errorMessage || "Не удалось создать заказ по ЭЗЗ.",
            });
            return false;
        }

        alert({ type: "success", title: "Успешно!", message: "Заказ по ЭЗЗ создан." });
        return result;
    });

export const bindShippingOrder = (params: ShippingOrderBindModel): Promise<boolean> =>
    alertRequest(`${resource}/Bind`, body(params));

export const getOrganizationProcessingSettings = (): Promise<
    OrganizationShippingOrderProcessingSettingEditModel | false
> => request(`${resource}/GetOrganizationProcessingSettings`, { method: "GET" });

export const applyOrganizationProcessingSettings = (
    params: OrganizationShippingOrderProcessingSettingEditModel,
): Promise<boolean> => alertRequest(`${resource}/ApplyProcessingSettings`, body(params));

export const generateResponseTitleByBid = (
    params: ShippingOrderGenerateResponseTitleByBidModel,
): Promise<ShippingOrderTitleModel> => alertRequest(`${resource}/generateResponseTitleByBid`, body(params));

/** Generate a decline T2 without requiring an order, bid or driver. */
export const generateDeclineResponseTitle = (
    params: ShippingOrderGenerateDeclineResponseTitleModel,
): Promise<ShippingOrderTitleModel | false> => alertRequest(`${resource}/generateDeclineResponseTitle`, body(params));

export const getInfoToGenerateResponseTitle = (bidId: number): Promise<BidInfoToGenerateTitleModel | false> =>
    alertRequest(
        `${resource}/GetInfoToGenerateResponseTitle${queryParams({ bidId })}`,
        { method: "GET" },
        {
            successAlerts: false,
            noContentAlerts: true,
        },
    );

export const applyInfoToGenerateResponseTitle = (params: BidInfoToGenerateTitleEditModel): Promise<{} | false> =>
    alertRequest(`${resource}/ApplyInfoToGenerateResponseTitle`, body(params), {
        successAlerts: false,
        noContentAlerts: true,
    });

export const applySignature = (params: {}) => alertRequest(`${resource}/applySignature`, params);

export const getPrintFormFromExternal = (shippingOrderId: number): Promise<Response> =>
    request(
        `${resource}/getPrintFormFromExternal?shippingOrderId=${shippingOrderId}`,
        { method: "GET" },
        { withoutMiddleware: true, withoutConnectionAlert: true },
    );
